//@flow

import type {TriggerFields} from '../components/TriggerForm/const';
import type {
  TriggerFilterType,
  TriggerType,
  TriggerFiltersFormValues,
} from '../../../../entities';
import {
  getActionTypeForTriggerType,
  getDefaultFiltersForTriggerType,
  getAllowedFiltersForTriggerType,
  isCustomerInactivityTrigger,
  isDefaultFilter,
  isMessageReceivedTrigger,
  FILTER_TYPES,
  MAX_CUSTOMER_INACTIVITY_DAYS,
  supportsMultipleFilters,
  DEFAULT_TRIGGER_TYPE,
} from './triggerConfig';
import {normalizeTriggerName} from './adapters';

function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

type ValidationResult = {
  valid: boolean,
  errors: {[key: string]: string},
};

const MAX_TOTAL_FILES = 10;

export function validateTriggerForm(data: TriggerFields): ValidationResult {
  const errors = {};

  const normalizedId = normalizeTriggerName(data.id || '');

  if (!normalizedId) {
    errors.id = 'El identificador es requerido';
  } else if (!/^[a-z0-9_]+$/i.test(normalizedId)) {
    errors.id =
      'El identificador guardado solo puede tener letras, números y guiones bajos';
  }

  if (!data.triggerType) {
    errors.triggerType = 'El tipo de disparador es requerido';
  }

  const triggerType: TriggerType = data.triggerType || DEFAULT_TRIGGER_TYPE;
  const defaultFilters = getDefaultFiltersForTriggerType(triggerType);
  const filters = data.filters || {};

  const missingDefault = defaultFilters.some(
    filterType => !filters[filterType]
  );
  if (missingDefault) {
    errors.filters = 'Falta el filtro requerido para este tipo de disparador';
  }

  const textSearchFilter = filters[FILTER_TYPES.TRIGGER_TEXT_SEARCH];
  if (isMessageReceivedTrigger(triggerType) || textSearchFilter) {
    const textSearch = textSearchFilter;
    if (textSearch) {
      if (!textSearch.searchText?.trim()) {
        errors[`filters.${FILTER_TYPES.TRIGGER_TEXT_SEARCH}.searchText`] =
          'Este campo es requerido para crear una regla';
      }
      if (!textSearch.searchTextPattern) {
        errors[
          `filters.${FILTER_TYPES.TRIGGER_TEXT_SEARCH}.searchTextPattern`
        ] = 'El patrón de búsqueda es requerido';
      }
      if (textSearch.enabledRelatedTexts) {
        const validRelatedTexts = (textSearch.relatedSearchTexts || [])
          .map(word => word.trim())
          .filter(Boolean);

        if (validRelatedTexts.length === 0) {
          errors[
            `filters.${FILTER_TYPES.TRIGGER_TEXT_SEARCH}.relatedSearchTexts`
          ] = 'Agrega al menos una palabra similar válida';
        }
      }
    }
  }

  const inactivityFilter = filters[FILTER_TYPES.CUSTOMER_INACTIVITY];
  if (isCustomerInactivityTrigger(triggerType) || inactivityFilter) {
    const inactivity = inactivityFilter;
    if (inactivity) {
      if (inactivity.inactiveDays == null) {
        errors[`filters.${FILTER_TYPES.CUSTOMER_INACTIVITY}.inactiveDays`] =
          'Los días de inactividad son requeridos';
      } else if (
        inactivity.inactiveDays < 0 ||
        inactivity.inactiveDays > MAX_CUSTOMER_INACTIVITY_DAYS
      ) {
        errors[
          `filters.${FILTER_TYPES.CUSTOMER_INACTIVITY}.inactiveDays`
        ] = `Los días de inactividad deben estar entre 0 y ${MAX_CUSTOMER_INACTIVITY_DAYS}`;
      }
    }

    if (!data.executionTimeSlot) {
      errors.executionTimeSlot = 'La hora de ejecución es requerida';
    }
  }

  const segmentFilter = filters[FILTER_TYPES.CRM_SEGMENT];
  if (segmentFilter && !segmentFilter.segmentId) {
    errors[`filters.${FILTER_TYPES.CRM_SEGMENT}.segmentId`] =
      'Selecciona un segmento';
  }

  const tagsFilter = filters[FILTER_TYPES.CRM_TAGS];
  if (tagsFilter && !tagsFilter.tagIds?.length) {
    errors[`filters.${FILTER_TYPES.CRM_TAGS}.tagIds`] =
      'Selecciona al menos una etiqueta';
  }

  const funnelFilter = filters[FILTER_TYPES.CRM_FUNNEL];
  if (funnelFilter) {
    if (!funnelFilter.funnelId) {
      errors[`filters.${FILTER_TYPES.CRM_FUNNEL}.funnelId`] =
        'Selecciona un embudo';
    }
    if (!funnelFilter.funnelLaneId) {
      errors[`filters.${FILTER_TYPES.CRM_FUNNEL}.funnelLaneId`] =
        'Selecciona una etapa';
    }
  }

  const actionType = getActionTypeForTriggerType(triggerType);
  const needsFreeTextMessage =
    actionType === 'SEND_MESSAGE' ||
    (actionType === 'SEND_INIT_MESSAGE' && data.provider === 'WHATSAPP_WEB');

  if (actionType === 'SEND_INIT_MESSAGE') {
    if (!data.provider) {
      errors.provider = 'Selecciona un proveedor';
    }
    if (!data.deviceId) {
      errors.deviceId = 'Selecciona un dispositivo';
    }

    if (data.provider === 'WHATSAPP_API') {
      if (!data.templateName) {
        errors.templateName = 'Selecciona un template';
      }
      if (!Array.isArray(data.templateComponents)) {
        errors.templateComponents =
          'Configura las variables del template antes de guardar';
      }
    }
  }

  if (needsFreeTextMessage && !stripHtml(data.response || '')) {
    errors.response = 'El mensaje de respuesta es requerido';
  }

  const totalFiles =
    (data.attachments?.length || 0) +
    (data.savedDocuments?.length || 0) +
    (data.savedImages?.length || 0) +
    (data.savedVideos?.length || 0);

  if (needsFreeTextMessage && totalFiles > MAX_TOTAL_FILES) {
    errors.attachments = 'Máximo 10 archivos en total';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function getActiveFilterTypes(
  filters: TriggerFiltersFormValues
): TriggerFilterType[] {
  const filterTypes = Object.keys(filters || {});

  return filterTypes.filter(filterType => filters[filterType] != null);
}

export function getOptionalFiltersToAdd(
  triggerType: TriggerType,
  activeFilters: TriggerFilterType[]
): TriggerFilterType[] {
  if (!supportsMultipleFilters(triggerType)) return [];

  return getAllowedFiltersForTriggerType(triggerType).filter(
    filterType =>
      !isDefaultFilter(triggerType, filterType) &&
      !activeFilters.includes(filterType)
  );
}
